import type { Workspace } from '../types/workspace'
import { isSurfaceAllowed, type WorkspaceSurfaceKey, type WorkspaceSurfaceMask } from '../types/workspaceSurfaceMask'
import type { NlsKey } from '../nls/strings_nls'
import { OPS_SECTOR_KEYS, OPS_SECTORS, isBookingSector, type OpsSector } from '../types/ops'

export type ModuleKey =
  | 'stock'
  | 'budget'
  | 'quota'
  | 'forms'
  | 'meetings'
  | 'automate'
  | 'linkHub'
  | 'upload'
  | 'design'
  | 'analysis'
  | 'reports'
  | 'funPack'
  | 'teaching'
  | 'training'
  | 'clientOutreach'
  | 'construction'
  | 'hospital'
  | 'sites'
  | 'timesheets'
  | 'delivery'
  | 'shops'
  | 'stays'
  | 'jobs'
  | 'clinics'
  | 'plant'

export const MODULE_KEYS: readonly ModuleKey[] = [
  'stock',
  'budget',
  'quota',
  'forms',
  'meetings',
  'automate',
  'linkHub',
  'upload',
  'design',
  'analysis',
  'reports',
  'funPack',
  'teaching',
  'training',
  'clientOutreach',
  'construction',
  'hospital',
  'sites',
  'timesheets',
  'delivery',
  'shops',
  'stays',
  'jobs',
  'clinics',
  'plant',
] as const

const OFF_WHEN_MISSING_MODULE_KEYS: ReadonlySet<ModuleKey> = new Set([
  'stock',
  'budget',
  'quota',
  'teaching',
  'training',
  'clientOutreach',
  'construction',
  'hospital',
  'sites',
  'timesheets',
  'delivery',
  'shops',
  'stays',
  'jobs',
  'clinics',
  'plant',
])

const MODULE_TO_SURFACE: Partial<Record<ModuleKey, WorkspaceSurfaceKey>> = {
  stock: 'STOCK',
  budget: 'BUDGET',
  quota: 'QUOTA',
  forms: 'FORMS',
  meetings: 'MEETINGS',
  automate: 'AUTOMATE',
  design: 'DESIGN',
  analysis: 'ANALYSIS',
  reports: 'REPORTS',
  funPack: 'RAID',
  teaching: 'TEACHING',
  training: 'TRAINING',
  construction: 'CONSTRUCTION',
  hospital: 'HOSPITAL',
  sites: 'SITES',
  delivery: 'DELIVERY',
  shops: 'SHOPS',
  stays: 'STAYS',
  jobs: 'JOBS',
  clinics: 'CLINICS',
  plant: 'PLANT',
}

const MODULE_FLAG_PROP: Record<ModuleKey, keyof NonNullable<Workspace['modules']>> = {
  stock: 'stockEnabled',
  budget: 'budgetEnabled',
  quota: 'quotaEnabled',
  forms: 'formsEnabled',
  meetings: 'meetingsEnabled',
  automate: 'automateEnabled',
  linkHub: 'linkHubEnabled',
  upload: 'uploadEnabled',
  design: 'designEnabled',
  analysis: 'analysisEnabled',
  reports: 'reportsEnabled',
  funPack: 'funPackEnabled',
  teaching: 'teachingEnabled',
  training: 'trainingEnabled',
  clientOutreach: 'clientOutreachEnabled',
  construction: 'constructionEnabled',
  hospital: 'hospitalEnabled',
  sites: 'sitesEnabled',
  timesheets: 'timesheetsEnabled',
  delivery: 'deliveryEnabled',
  shops: 'shopsEnabled',
  stays: 'staysEnabled',
  jobs: 'jobsEnabled',
  clinics: 'clinicsEnabled',
  plant: 'plantEnabled',
}

export type WorkspaceModuleSource = {
  modules?: Workspace['modules']
  isPreview?: boolean
  previewSurfaceMask?: WorkspaceSurfaceMask
}

/**
 * Single source of truth for whether a module is enabled in a workspace (D6).
 *
 * Rules:
 * - Preview workspaces follow their preview surface mask via isSurfaceAllowed.
 * - Stock, Budget, and Quota default to OFF when the flag is missing (=== true).
 * - All other modules default to ON when the flag is missing (!== false).
 */
export function isModuleEnabled(
  workspace: WorkspaceModuleSource | null | undefined,
  key: ModuleKey,
): boolean {
  if (workspace?.isPreview) {
    const surface = MODULE_TO_SURFACE[key]
    if (surface) {
      return isSurfaceAllowed(workspace.previewSurfaceMask, surface)
    }
  }

  const prop = MODULE_FLAG_PROP[key]
  const rawValue = workspace?.modules?.[prop]

  if (OFF_WHEN_MISSING_MODULE_KEYS.has(key)) {
    return rawValue === true
  }
  return rawValue !== false
}

/**
 * Checks whether any bookings-enabled sector module is active for this workspace.
 */
export function isSectorBookingsEnabled(
  workspace: WorkspaceModuleSource | null | undefined,
): boolean {
  return OPS_SECTOR_KEYS.some((s) => isBookingSector(s) && isModuleEnabled(workspace, OPS_SECTORS[s].moduleKey))
}

/**
 * Checks whether any ops sector module is active for this workspace.
 */
export function isAnyOpsSectorEnabled(
  workspace: WorkspaceModuleSource | null | undefined,
): boolean {
  return OPS_SECTOR_KEYS.some((s) => isModuleEnabled(workspace, OPS_SECTORS[s].moduleKey))
}

/**
 * Returns all bookings-enabled sectors that are active in this workspace.
 */
export function getEnabledBookingSectors(
  workspace: WorkspaceModuleSource | null | undefined,
): OpsSector[] {
  return OPS_SECTOR_KEYS.filter((s) => isBookingSector(s) && isModuleEnabled(workspace, OPS_SECTORS[s].moduleKey))
}

const VIEW_TO_MODULE: Record<string, ModuleKey> = {
  STOCK: 'stock',
  BUDGET: 'budget',
  QUOTA: 'quota',
  FORMS: 'forms',
  MEETINGS: 'meetings',
  AUTOMATE: 'automate',
  DESIGN: 'design',
  UPLOAD: 'upload',
  LINK: 'linkHub',
  ANALYSIS: 'analysis',
  REPORTS: 'reports',
  RAID: 'funPack',
  TEACHING: 'teaching',
  TRAINING: 'training',
  CLIENTS: 'clientOutreach',
  CONSTRUCTION: 'construction',
  HOSPITAL: 'hospital',
  SITES: 'sites',
  TIMESHEETS: 'timesheets',
  DELIVERY: 'delivery',
  SHOPS: 'shops',
  STAYS: 'stays',
  JOBS: 'jobs',
  CLINICS: 'clinics',
  PLANT: 'plant',
}

export function moduleForLayoutView(view: string): ModuleKey | null {
  return VIEW_TO_MODULE[view] ?? null
}

export function switchedOffModuleForView(
  view: string,
  switchedOff: ReadonlySet<ModuleKey>,
): ModuleKey | null {
  const mod = moduleForLayoutView(view)
  if (mod && switchedOff.has(mod)) {
    return mod
  }
  return null
}

export function switchedOffModulesFor(
  workspace: WorkspaceModuleSource | null | undefined,
): ReadonlySet<ModuleKey> {
  if (!workspace || workspace.isPreview) {
    return new Set<ModuleKey>()
  }
  const result = new Set<ModuleKey>()
  for (const key of MODULE_KEYS) {
    if (!isModuleEnabled(workspace, key)) {
      result.add(key)
    }
  }
  return result
}

const OVERVIEW_SECTION_TO_MODULE: Record<string, ModuleKey> = {
  FORMS: 'forms',
  BUDGET: 'budget',
  BUDGET_BREAKDOWN: 'budget',
}

export function overviewSectionModule(kind: string): ModuleKey | null {
  return OVERVIEW_SECTION_TO_MODULE[kind] ?? null
}

const TEMPLATE_CATEGORY_TO_MODULE: Record<string, ModuleKey> = {
  Designs: 'design',
  Automations: 'automate',
  Forms: 'forms',
}

export function templateCategoryModule(category: string): ModuleKey | null {
  return TEMPLATE_CATEGORY_TO_MODULE[category] ?? null
}

export type ExtendedWorkspaceUseCase =
  | 'PERSONAL_GOALS'
  | 'TEAM_PROJECTS'
  | 'STUDY_PLANNING'
  | 'CONTENT_PLANNING'
  | 'STARTUP_BUILDING'
  | 'OTHER'
  | 'FIELD_TEAM'
  | 'SHOP_STOCK'
  | 'EVENTS'
  | 'SCHOOL'
  | 'TUITION_COACHING'
  | 'ACADEMY_TRAINING'
  | 'CORPORATE_TRAINING'
  | 'AGENCY'
  | 'CONSTRUCTION_CONTRACTOR'
  | 'INTERIOR_FITOUT'
  | 'CLINIC'
  | 'HOSPITAL'
  | 'FACTORY'
  | 'FACILITY_SECURITY'
  | 'SOFTWARE_DELIVERY'

export type WorkspaceModulesPreset = {
  stockEnabled: boolean
  budgetEnabled: boolean
  quotaEnabled: boolean
  formsEnabled: boolean
  meetingsEnabled: boolean
  automateEnabled: boolean
  linkHubEnabled: boolean
  uploadEnabled: boolean
  designEnabled: boolean
  analysisEnabled: boolean
  reportsEnabled: boolean
  funPackEnabled: boolean
  helperScanEnabled: boolean
  helperCountEnabled: boolean
  helperFormsEnabled: boolean
  teachingEnabled: boolean
  trainingEnabled: boolean
  clientOutreachEnabled: boolean
  constructionEnabled: boolean
  hospitalEnabled: boolean
  sitesEnabled: boolean
  timesheetsEnabled: boolean
}

export interface WorkspaceUseCaseOption {
  value: ExtendedWorkspaceUseCase
  labelKey: NlsKey
  hintKey: NlsKey
  iconKey: string
  teamOnly: boolean
}

export const WORKSPACE_USE_CASE_OPTIONS: WorkspaceUseCaseOption[] = [
  {
    value: 'PERSONAL_GOALS',
    labelKey: 'NLSSetupUseCasePersonalGoals',
    hintKey: 'NLSSetupUseCasePersonalGoalsHint',
    iconKey: 'person',
    teamOnly: false,
  },
  {
    value: 'TEAM_PROJECTS',
    labelKey: 'NLSSetupUseCaseTeamProjects',
    hintKey: 'NLSSetupUseCaseTeamProjectsHint',
    iconKey: 'groups',
    teamOnly: false,
  },
  {
    value: 'STUDY_PLANNING',
    labelKey: 'NLSSetupUseCaseStudyPlanning',
    hintKey: 'NLSSetupUseCaseStudyPlanningHint',
    iconKey: 'school',
    teamOnly: false,
  },
  {
    value: 'CONTENT_PLANNING',
    labelKey: 'NLSSetupUseCaseContentPlanning',
    hintKey: 'NLSSetupUseCaseContentPlanningHint',
    iconKey: 'content',
    teamOnly: false,
  },
  {
    value: 'STARTUP_BUILDING',
    labelKey: 'NLSSetupUseCaseStartupBuilding',
    hintKey: 'NLSSetupUseCaseStartupBuildingHint',
    iconKey: 'startup',
    teamOnly: false,
  },
  {
    value: 'OTHER',
    labelKey: 'NLSSetupUseCaseOther',
    hintKey: 'NLSSetupUseCaseOtherHint',
    iconKey: 'other',
    teamOnly: false,
  },
  {
    value: 'FIELD_TEAM',
    labelKey: 'NLSSetupUseCaseFieldTeam',
    hintKey: 'NLSSetupUseCaseFieldTeamHint',
    iconKey: 'field',
    teamOnly: true,
  },
  {
    value: 'SHOP_STOCK',
    labelKey: 'NLSSetupUseCaseShopStock',
    hintKey: 'NLSSetupUseCaseShopStockHint',
    iconKey: 'shop',
    teamOnly: true,
  },
  {
    value: 'EVENTS',
    labelKey: 'NLSSetupUseCaseEvents',
    hintKey: 'NLSSetupUseCaseEventsHint',
    iconKey: 'events',
    teamOnly: true,
  },
  {
    value: 'SCHOOL',
    labelKey: 'NLSSetupUseCaseSchool',
    hintKey: 'NLSSetupUseCaseSchoolHint',
    iconKey: 'school_edu',
    teamOnly: true,
  },
  {
    value: 'TUITION_COACHING',
    labelKey: 'NLSSetupUseCaseTuitionCoaching',
    hintKey: 'NLSSetupUseCaseTuitionCoachingHint',
    iconKey: 'coaching',
    teamOnly: true,
  },
  {
    value: 'ACADEMY_TRAINING',
    labelKey: 'NLSSetupUseCaseAcademyTraining',
    hintKey: 'NLSSetupUseCaseAcademyTrainingHint',
    iconKey: 'academy',
    teamOnly: true,
  },
  {
    value: 'CORPORATE_TRAINING',
    labelKey: 'NLSSetupUseCaseCorporateTraining',
    hintKey: 'NLSSetupUseCaseCorporateTrainingHint',
    iconKey: 'corporate_training',
    teamOnly: true,
  },
  {
    value: 'AGENCY',
    labelKey: 'NLSSetupUseCaseAgency',
    hintKey: 'NLSSetupUseCaseAgencyHint',
    iconKey: 'agency',
    teamOnly: true,
  },
]

export const AUDIENCE_PARAM_TO_USE_CASE: Record<string, ExtendedWorkspaceUseCase> = {
  field: 'FIELD_TEAM',
  events: 'EVENTS',
  agencies: 'AGENCY',
  schools: 'SCHOOL',
  coaching: 'TUITION_COACHING',
}

export function resolveUseCaseFromAudienceParam(
  param: string | null | undefined,
): ExtendedWorkspaceUseCase | null {
  if (!param) return null
  return AUDIENCE_PARAM_TO_USE_CASE[param.toLowerCase()] ?? null
}

/**
 * Returns the §4.2 preset flags for every use case.
 * All keys are explicitly written. Fun pack is true in every new workspace.
 * When audience is SOLO_HELPERS, helperScan, helperCount, and helperForms are all turned on.
 */
export function modulesForUseCase(
  useCase: ExtendedWorkspaceUseCase | '' | null | undefined,
  audience?: 'SOLO' | 'SOLO_HELPERS' | 'TEAM' | null,
): WorkspaceModulesPreset {
  const base: WorkspaceModulesPreset = {
    stockEnabled: false,
    budgetEnabled: false,
    quotaEnabled: false,
    formsEnabled: false,
    meetingsEnabled: false,
    automateEnabled: false,
    linkHubEnabled: false,
    uploadEnabled: false,
    designEnabled: false,
    analysisEnabled: false,
    reportsEnabled: false,
    funPackEnabled: true,
    helperScanEnabled: true,
    helperCountEnabled: true,
    helperFormsEnabled: true,
    teachingEnabled: false,
    trainingEnabled: false,
    clientOutreachEnabled: false,
    constructionEnabled: false,
    hospitalEnabled: false,
    sitesEnabled: false,
    timesheetsEnabled: false,
  }

  let preset: WorkspaceModulesPreset
  switch (useCase) {
    case 'CONSTRUCTION_CONTRACTOR':
    case 'INTERIOR_FITOUT':
      preset = {
        ...base,
        constructionEnabled: true,
        stockEnabled: true,
        formsEnabled: true,
        reportsEnabled: true,
        meetingsEnabled: true,
      }
      break
    case 'CLINIC':
    case 'HOSPITAL':
      preset = {
        ...base,
        hospitalEnabled: true,
        stockEnabled: true,
        formsEnabled: true,
        reportsEnabled: true,
        meetingsEnabled: true,
      }
      break
    case 'FACTORY':
      preset = {
        ...base,
        stockEnabled: true,
        formsEnabled: true,
        reportsEnabled: true,
        uploadEnabled: true,
        analysisEnabled: true,
        meetingsEnabled: true,
      }
      break
    case 'FACILITY_SECURITY':
      preset = {
        ...base,
        formsEnabled: true,
        reportsEnabled: true,
        stockEnabled: true,
        uploadEnabled: true,
        analysisEnabled: true,
        meetingsEnabled: true,
      }
      break
    case 'SOFTWARE_DELIVERY':
      preset = {
        ...base,
        clientOutreachEnabled: true,
        formsEnabled: true,
        meetingsEnabled: true,
        reportsEnabled: true,
        linkHubEnabled: true,
        uploadEnabled: true,
        analysisEnabled: true,
      }
      break
    case 'TEAM_PROJECTS':
      preset = {
        ...base,
        formsEnabled: true,
        meetingsEnabled: true,
        reportsEnabled: true,
      }
      break
    case 'STARTUP_BUILDING':
      preset = {
        ...base,
        formsEnabled: true,
        meetingsEnabled: true,
        budgetEnabled: true,
        reportsEnabled: true,
        analysisEnabled: true,
      }
      break
    case 'CONTENT_PLANNING':
      preset = {
        ...base,
        designEnabled: true,
        formsEnabled: true,
      }
      break
    case 'FIELD_TEAM':
      preset = {
        ...base,
        quotaEnabled: true,
        timesheetsEnabled: true,
        formsEnabled: true,
        meetingsEnabled: true,
        helperScanEnabled: true,
        helperFormsEnabled: true,
        helperCountEnabled: false,
      }
      break
    case 'SHOP_STOCK':
      preset = {
        ...base,
        stockEnabled: true,
        budgetEnabled: true,
        formsEnabled: true,
        helperCountEnabled: true,
        helperScanEnabled: true,
        helperFormsEnabled: false,
      }
      break
    case 'EVENTS':
      preset = {
        ...base,
        budgetEnabled: true,
        timesheetsEnabled: true,
        formsEnabled: true,
        meetingsEnabled: true,
        designEnabled: true,
        helperFormsEnabled: true,
        helperScanEnabled: false,
        helperCountEnabled: false,
      }
      break
    case 'SCHOOL':
    case 'TUITION_COACHING':
      preset = {
        ...base,
        teachingEnabled: true,
        timesheetsEnabled: true,
        formsEnabled: true,
        meetingsEnabled: true,
        reportsEnabled: true,
        budgetEnabled: true,
      }
      break
    case 'ACADEMY_TRAINING':
      preset = {
        ...base,
        trainingEnabled: true,
        timesheetsEnabled: true,
        formsEnabled: true,
        meetingsEnabled: true,
        quotaEnabled: true,
        budgetEnabled: true,
      }
      break
    case 'CORPORATE_TRAINING':
      preset = {
        ...base,
        trainingEnabled: true,
        timesheetsEnabled: true,
        formsEnabled: true,
        meetingsEnabled: true,
        reportsEnabled: true,
      }
      break
    case 'AGENCY':
      preset = {
        ...base,
        formsEnabled: true,
        meetingsEnabled: true,
        reportsEnabled: true,
        analysisEnabled: true,
        clientOutreachEnabled: true,
        timesheetsEnabled: true,
        funPackEnabled: true,
      }
      break
    case 'PERSONAL_GOALS':
    case 'STUDY_PLANNING':
    case 'OTHER':
    case '':
    case null:
    case undefined:
    default:
      preset = base
      break
  }

  if (audience === 'SOLO_HELPERS') {
    return {
      ...preset,
      helperScanEnabled: true,
      helperCountEnabled: true,
      helperFormsEnabled: true,
    }
  }

  return preset
}

// Long enough to press the "Turn on" action; the default is 3.2 s, src/context/ToastContext.tsx:4.
export const MODULE_TOAST_DURATION_MS = 8000
